<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useTestRenderStore } from '@stores';
import { useDateFormat } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

const { t } = useI18n({ useScope: 'global' });

const renderStore = useTestRenderStore();
const route = useRoute();
const isLoading = ref(true);

const examInfo = ref({});
const proctoringInfo = ref({});
const examId = route.query.exam_id;

const examResultHandler = async (examId) => {
    const result = await renderStore.getExamResult(examId);
    examInfo.value = result.examInfo;
    proctoringInfo.value = result.proctoringInfo;
    isLoading.value = false;
};

const isPassedFinal = computed(() => {
    const examPassed = examInfo.value?.isPassed;
    const proctorPassed = proctoringInfo.value?.isPassed;

    if (proctoringInfo.value && proctorPassed !== undefined) {
        return examPassed && proctorPassed;
    }
    return examPassed;
});

onMounted(() => {
    examResultHandler(examId);
});
</script>

<template>
    <div class="exam-result-container">
        <Spinner v-if="isLoading" class="loading-wrapper" />
        <div class="exam-result" v-else>
            <div class="icon-container" :class="isPassedFinal ? ' bg-green-200' : ' bg-red-200'">
                <i :class="isPassedFinal ? 'pi pi-check-circle pass-icon' : 'pi pi-times-circle fail-icon'"></i>
            </div>
            <h2 class="result-title">{{ examInfo.isPassed ? t('tests.passExam') : t('tests.failExam') }}</h2>

            <div class="result-content">
                <h3>{{ t('tests.testScore') }}</h3>
                <h1 :class="examInfo.isPassed ? 'pass-text' : ' fail-text'">{{ examInfo.earnedPercent }}%</h1>

                <ProgressBar :value="examInfo.earnedPercent" class="progress-bar"></ProgressBar>

                <div class="progress-labels">
                    <span>0%</span>
                    <span class="passing-mark">{{ examInfo.passingPercent }}% {{ t('tests.passing') }}</span>
                    <span>100%</span>
                </div>

                <div class="exam-details">
                    <div class="detail">
                        <strong>{{ t('tests.correct') }}:</strong> {{ examInfo.correct }}
                    </div>
                    <div class="detail">
                        <strong>{{ t('tests.incorrect') }}:</strong> {{ examInfo.incorrect }}
                    </div>
                    <div class="detail" v-if="examInfo.unanswered !== undefined">
                        <strong>{{ t('tests.unanswered') }}:</strong> {{ examInfo.unanswered }}
                    </div>
                    <div class="detail">
                        <strong>{{ t('tests.total') }}:</strong> {{ examInfo.total }}
                    </div>
                </div>
            </div>
            <div class="result-content" v-if="proctoringInfo">
                <h3>{{ t('tests.proctorScore') }}</h3>
                <h1 :class="proctoringInfo.isPassed ? 'pass-text' : ' fail-text'">{{ proctoringInfo.earnedPercent }}%</h1>

                <ProgressBar :value="proctoringInfo.earnedPercent" class="progress-bar"></ProgressBar>

                <div class="progress-labels">
                    <span>0%</span>
                    <span class="passing-mark">{{ proctoringInfo.passingPercent }}% {{ t('tests.passing') }}</span>
                    <span>100%</span>
                </div>

                <div class="exam-details">
                    <div class="detail">
                        <strong>{{ t('global.duration') }}:</strong> {{ proctoringInfo.duration }}
                    </div>
                    <div class="detail">
                        <strong>{{ t('global.start') }}:</strong> {{ proctoringInfo.startedAt ? useDateFormat(proctoringInfo.startedAt, 'HH:mm').value : '-' }}
                    </div>
                    <div class="detail">
                        <strong>{{ t('global.end') }}:</strong> {{ proctoringInfo.stoppedAt ? useDateFormat(proctoringInfo.stoppedAt, 'HH:mm').value : '-' }}
                    </div>
                </div>

                <Accordion value="0">
                    <AccordionPanel>
                        <AccordionHeader>{{ t('tests.proctorDetails') }}</AccordionHeader>
                        <AccordionContent>
                            <div class="proctoring-details">
                                <div class="proctoring-content" v-for="(value, key) in proctoringInfo.averages" :key="key">
                                    <p>{{ t(`violations.${key}.long`) }}</p>
                                    <p>{{ value }}</p>
                                </div>
                            </div>
                        </AccordionContent>
                    </AccordionPanel>
                </Accordion>
            </div>
        </div>
    </div>
</template>

<style scoped>
.loading-wrapper {
    @apply h-screen;
}
.exam-result-container {
    @apply min-h-screen flex items-start justify-center py-10;
}
.exam-result {
    @apply text-center max-w-[600px] mx-auto p-16 flex flex-col gap-6 items-center w-full border border-gray-200 rounded-xl bg-white;
}

.icon-container {
    @apply w-fit p-8 rounded-full;
}

.pass-icon,
.fail-icon,
.pass-text,
.fail-text {
    @apply text-6xl font-bold;
}
.pass-icon,
.pass-text {
    @apply text-green-500;
}
.fail-icon,
.fail-text {
    @apply text-red-500;
}
.result-title {
    @apply text-2xl font-bold mb-4;
}
.result-content {
    @apply flex flex-col gap-2 w-full;
}
.progress-bar {
    @apply w-full;
}
.progress-labels {
    @apply flex justify-between w-full;
}

.exam-details {
    @apply flex flex-wrap justify-center gap-2 mt-4;
}
.detail {
    @apply text-sm font-medium bg-gray-100 px-4 py-2 rounded-md;
}
.passing-mark {
    @apply text-orange-400 font-bold;
}
.proctoring-details {
    @apply flex flex-col gap-2;
}
.proctoring-content {
    @apply flex justify-between items-center;
}
</style>
